import type { ValidationConfig } from '../../../core/dataProcessor/types';
import { StockData } from '../../../../helper/hxKLine';

type Concept = {
  rect: unknown;
  text: {
    name: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

// 🆕 为新 ValidationEngine 导出的配置
export const hxKLineAndTagValidationConfig: ValidationConfig = {
  fields: [
    { field: 'from', type: 'date', required: true },
    { field: 'to', type: 'date', required: true },
    { field: 'concepts', type: 'array', required: true }
  ],
  businessRules: [
    {
      name: 'hx-kline-and-tag-stocks-required',
      validator: (data, layer) => {
        const hxKLineLayer = layer as { stocks?: StockData[] };
        return Array.isArray(hxKLineLayer.stocks) && hxKLineLayer.stocks.length > 0;
      },
      message: 'K-line and tag chart requires stock configuration',
      suggestion: 'Provide stocks array with at least one valid stock configuration',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-tag-stock-properties',
      validator: (data, layer) => {
        const hxKLineLayer = layer as { stocks?: StockData[] };
        if (!Array.isArray(hxKLineLayer.stocks)) {
          return true;
        }

        return hxKLineLayer.stocks.every(
          (stock: StockData) =>
            stock.code &&
            typeof stock.code === 'string' &&
            stock.market &&
            typeof stock.market === 'string'
        );
      },
      message: 'All stocks must have valid code and market properties as strings',
      suggestion: 'Ensure each stock has both code and market properties as string values',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-tag-trade-date-format',
      validator: (data, layer) => {
        const hxKLineLayer = layer as { tradeDate?: string[] };
        if (!hxKLineLayer.tradeDate) {
          return true;
        }

        return (
          Array.isArray(hxKLineLayer.tradeDate) &&
          hxKLineLayer.tradeDate.length === 2 &&
          hxKLineLayer.tradeDate.every(date => typeof date === 'string')
        );
      },
      message: 'Trade date must be an array of two date strings',
      suggestion:
        'Provide tradeDate as an array containing start and end date strings [startDate, endDate]',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-tag-date-range-validity',
      validator: (data, layer) => {
        if (!Array.isArray(data) || data.length === 0) {
          return true;
        }

        const { encoding } = layer;
        // 检查from和to字段的日期范围是否合理
        return data.every((item: Record<string, unknown>) => {
          const fromDate = new Date(item[encoding?.from as string] as string);
          const toDate = new Date(item[encoding?.to as string] as string);

          return (
            !Number.isNaN(fromDate.getTime()) &&
            !Number.isNaN(toDate.getTime()) &&
            fromDate <= toDate
          );
        });
      },
      message: 'Tag date range must be valid (from date <= to date)',
      suggestion: 'Ensure from date is not later than to date for each tag',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-tag-concepts-structure',
      validator: (data, layer) => {
        if (!Array.isArray(data) || data.length === 0) {
          return true;
        }

        const { encoding } = layer;
        const conceptsField = encoding?.concepts as string;

        const hasValidText = (concept: Concept): boolean => {
          return concept.text && typeof concept.text.name === 'string';
        };

        const isValidConcept = (concept: Concept): boolean => {
          if (!concept || typeof concept !== 'object' || !concept.rect) {
            return false;
          }
          return hasValidText(concept);
        };

        return data.every((item: Record<string, unknown>) => {
          const concepts = item[conceptsField] as Concept[];
          return Array.isArray(concepts) && concepts.every(isValidConcept);
        });
      },
      message: 'Concepts field must contain valid concept objects with rect and text properties',
      suggestion: 'Ensure each concept has rect and text properties with text.name as string',
      severity: 'warning'
    },
    {
      name: 'hx-kline-and-tag-sufficient-data',
      validator: data => Array.isArray(data) && data.length >= 1,
      message: 'K-line and tag chart requires at least one tag',
      suggestion: 'Provide at least one tag event with from, to, and concepts fields',
      severity: 'warning'
    },
    {
      name: 'hx-kline-and-tag-optimal-tag-count',
      validator: data => !Array.isArray(data) || data.length <= 50,
      message: 'K-line and tag chart works best with 50 or fewer tags',
      suggestion: 'Consider limiting to 50 tags for better performance and readability',
      severity: 'warning'
    }
  ]
};
